import {Injectable} from '@angular/core';
import {Subject, Subscription} from 'rxjs';


// CANNOT DO THIS DUE TO CIRCULAR DEPENDENCY...
// import { GlobalService } from '../global/global.service';

/**
 * A custom Events service just like Ionic 3 Events https://ionicframework.com/docs/v3/api/util/Events/ which got removed in Ionic 5.
 *
 * ref:  https://medium.com/wizpanda/dealing-with-breaking-change-in-ionic-5-db3ba711dfcd
 * breaking change: https://github.com/ionic-team/ionic/blob/45d03baf981d0e10eb1fe689908532adef2ba31d/BREAKING.md#events-1
 * 
 * @author Shashank Agrawal
 */
@Injectable({
    providedIn: 'root'
})
export class Events 
{
    public consoleLog:boolean = false; 
    private subjects: Record<string, Subject<any>> = {};
    private subscriptions: Record<string, Subscription> = {};

    /**
     * Subscribe to a subjectName and provide a single handler/observer.
     * @param subjectName The name of the subjectName to subscribe to.
     * @param observer The observer or callback function to listen when changes are published.
     *
     * @returns Subscription from which you can unsubscribe to release memory resources and to prevent memory leak.
     */
    subscribe(context:string, subjectName: string, observer: (_: any) => void): Subscription 
    {
        const subscriptionKey = this.makeSubscriptionKey(subjectName, context);
        if (this.subscriptions[subscriptionKey]) {
            if (this.consoleLog) console.log('Events.subscribe: Subscription to ' + subjectName + ' via ' + context + ' ALREADY EXISTS', this.subscriptions);
            return;
        }

        const subject = this.ensureSubject(subjectName);
        const subscription = subject.subscribe(observer);
        this.subscriptions[subscriptionKey] = subscription;

        return subscription;
    }

    /**
     * Publish some data to the subscribers of the given subjectName.
     * @param subjectName The name of the subjectName to emit data to.
     * @param data data in any format to pass on.
     */
    publish(subjectName: string, data: any): void 
    {
        if (this.consoleLog) console.log('Events.publish: ' + subjectName, data);

        const subject = this.ensureSubject(subjectName);
        subject.next(data);
    }

    /**
     * When you are sure that you are done with the subjectName and the subscribers no longer needs to listen to a particular subjectName, you can
     * destroy the observable of the subjectName using this method.
     * @param subjectName The name of the subjectName to destroy.
     */
    destroy(subjectName: string): void 
    {
        if (this.consoleLog) console.log('Events.destroy: ' + subjectName);

        const subject = this.subjects[subjectName];
        if (!subject) {
            return;
        }

        const subscriptionKeys = Object.keys(this.subscriptions).filter(key => key.startsWith(`${subjectName}_`));

        for(let key in subscriptionKeys) {
            this.subscriptions[key].unsubscribe();
            delete this.subscriptions[key];
        }

        subject.complete();
        delete this.subjects[subjectName];
    }

    // PETER -- duplicate destroy logic above as all my other code was referring to "unsubscribe"
    unsubscribe(context:string, subjectName: string, ignored:any): void 
    {
        if (this.consoleLog) console.log('Events.unsubscribe: ' + subjectName + ' - ' + context);

        const subscriptionKey = this.makeSubscriptionKey(subjectName, context);
        if (!this.subscriptions[subscriptionKey]) {
            if (this.consoleLog) console.log('Events.unsubscribe: Subscription to ' + subjectName + ' via ' + context + ' NOT FOUND', this.subscriptions);
            return;
        }

        this.subscriptions[subscriptionKey].unsubscribe();
        delete this.subscriptions[subscriptionKey];
    }

    private ensureSubject(subjectName: string): Subject<any> {
        if (!this.subjects[subjectName]) 
        {
            if (this.consoleLog) console.log('Events.ensureTopic: ' + subjectName + ' ADDED', this.subjects);
            this.subjects[subjectName] = new Subject<any>();
        }

        return this.subjects[subjectName];
    }

    private makeSubscriptionKey(subjectName: string, context: string) {
        return `${subjectName}_${context}`;
    }
}

/*
  2. Changes in the subscribe method
  
      Before
        events.subscribe('user:created', (user, time) => {
            console.log('Welcome', user, 'at', time);
        });

      After
        this.events.subscribe(this.pageName, 'user:created', (data: any) => {
            console.log('Welcome', data.user, 'at', data.time);
        });

  3. Changes in the publish method

      Before
        this.events.publish('user:created', someUserInstance, Date.now());
  
      After
        this.events.publish('foo:user:logged-out', {
            user: someUserInstance,
            time: new Date()
        });

  a) Change the imports:
      
      Before
        import {Events} from 'ionic-angular';

      After
        import {Events} from '../your/path/to/service/events';
*/
